import { useState } from 'react'
import { format } from 'date-fns'
import { FileText, Presentation, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Document } from '@/lib/document-service'

interface DocumentListProps {
  documents: Document[]
  onEdit: (id: string) => void
  onDelete: (id: string) => void
}

function formatDate(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unknown date' : format(date, 'MMM d, yyyy')
}

export function DocumentList({ documents, onEdit, onDelete }: DocumentListProps) {
  const [open, setOpen] = useState(false)
  const [target, setTarget] = useState<string | null>(null)

  const askDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    setTarget(id)
    setOpen(true)
  }

  const confirm = () => {
    if (target) onDelete(target)
    setOpen(false)
    setTarget(null)
  }

  return (
    <>
      <div className="grid gap-4">
        {documents.map((doc) => (
          <button
            key={doc.id}
            type="button"
            className="flex w-full items-center justify-between rounded-lg border p-4 text-left hover:bg-accent"
            onClick={() => onEdit(doc.id)}
          >
            <div className="flex items-center space-x-4">
              <div className="rounded-full bg-primary/10 p-2">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium flex items-center gap-2">
                  {doc.title}
                  {doc.is_shared && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">
                      Shared
                    </span>
                  )}
                  {doc.is_readonly && (
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-semibold">
                      Read-only
                    </span>
                  )}
                  {doc.is_presentation && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-semibold flex items-center gap-1">
                      <Presentation className="h-3 w-3" />
                      Presentation
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  Last edited {formatDate(doc.updated_at)}
                </p>
              </div>
            </div>
            <Button
              asChild
              variant="ghost"
              size="icon"
              className="text-destructive"
              onClick={(e) => askDelete(e, doc.id)}
            >
              <span>
                <Trash2 className="h-4 w-4" />
              </span>
            </Button>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
